const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

const blobCache = new Map();
/**
 * One entry per file URL that is queued or downloading. Concurrent requests for the same
 * file share one entry (e.g. company logo shown in sidebar + header).
 * @type {Map<string, {fetchUrl: string, promise: Promise<string>, resolve: Function, reject: Function,
 *   waiters: number, started: boolean, controller: AbortController | null}>}
 */
const inFlightFetches = new Map();

/**
 * At most this many file downloads run at the same time. Browsers only open a few connections
 * per site, so without a limit a long list of images can make normal API calls (e.g. Complete
 * beam) wait behind them.
 */
const MAX_CONCURRENT_FETCHES = 3;
/** Entries waiting for a free download slot (front of the array = next to start). */
const pendingQueue = [];
let activeFetchCount = 0;

export function getAuthHeaders(extra = {}) {
  const headers = { ...extra };
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

/** True when the value is an inline or external URL that needs no authenticated fetch. */
export function isDirectMediaSrc(value) {
  const url = String(value || '').trim();
  if (!url) return false;
  if (/^data:/i.test(url)) return true;
  if (/^https?:\/\//i.test(url) && !url.includes('/api/files/')) return true;
  return false;
}

/** Builds the fetch URL for a stored file reference (key or /api/files path). */
export function resolveMediaFetchUrl(value) {
  const url = String(value || '').trim();
  if (!url || isDirectMediaSrc(url)) {
    return '';
  }
  if (url.startsWith('/api/files/')) {
    return `${API_BASE_URL}${url}`;
  }
  if (url.startsWith('/')) {
    return `${API_BASE_URL}${url}`;
  }
  const encoded = url.split('/').map((segment) => encodeURIComponent(segment)).join('/');
  return `${API_BASE_URL}/api/files/${encoded}`;
}

/** @deprecated Use resolveMediaFetchUrl; kept for link href construction. */
export function resolveFileUrl(value) {
  const direct = String(value || '').trim();
  if (!direct) return '';
  if (isDirectMediaSrc(direct)) return direct;
  return resolveMediaFetchUrl(direct);
}

/**
 * Key of the small list thumbnail for an uploaded design image, or '' when the image has none
 * (inline/external images, non-design files). Mirrors backend StorageKeyBuilder.designThumbnailKey:
 * designs/name-1234abcd.jpeg -> designs/thumbs/name-1234abcd.jpeg.jpg
 */
export function designThumbnailKey(value) {
  const key = String(value || '').trim();
  if (!key || isDirectMediaSrc(key)) return '';
  if (!key.startsWith('designs/') || key.startsWith('designs/thumbs/') || key === 'designs/') return '';
  return `designs/thumbs/${key.slice('designs/'.length)}.jpg`;
}

/** Returns the already-downloaded blob URL for a file, or '' when it is not cached yet. */
export function peekCachedBlobUrl(value) {
  const fetchUrl = resolveMediaFetchUrl(value);
  if (!fetchUrl) return '';
  return blobCache.get(fetchUrl) || '';
}

function pumpQueue() {
  while (activeFetchCount < MAX_CONCURRENT_FETCHES && pendingQueue.length > 0) {
    startFetch(pendingQueue.shift());
  }
}

function startFetch(entry) {
  entry.started = true;
  activeFetchCount += 1;
  entry.controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  fetch(entry.fetchUrl, {
    headers: getAuthHeaders(),
    signal: entry.controller ? entry.controller.signal : undefined,
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error('Failed to load file');
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      // Only cache when this download is still the current one for the URL
      // (it may have been invalidated or cancelled meanwhile).
      if (inFlightFetches.get(entry.fetchUrl) === entry) {
        blobCache.set(entry.fetchUrl, blobUrl);
      }
      entry.resolve(blobUrl);
    })
    .catch((err) => entry.reject(err))
    .finally(() => {
      activeFetchCount -= 1;
      if (inFlightFetches.get(entry.fetchUrl) === entry) {
        inFlightFetches.delete(entry.fetchUrl);
      }
      pumpQueue();
    });
}

function createEntry(fetchUrl) {
  const entry = {
    fetchUrl,
    promise: null,
    resolve: null,
    reject: null,
    waiters: 0,
    started: false,
    controller: null,
  };
  entry.promise = new Promise((resolve, reject) => {
    entry.resolve = resolve;
    entry.reject = reject;
  });
  // Cancelled downloads reject with nobody listening; avoid "unhandled rejection" noise.
  entry.promise.catch(() => {});
  return entry;
}

function cancelledError() {
  const err = new Error('File request cancelled');
  err.name = 'AbortError';
  return err;
}

/**
 * Queues an authenticated file download and returns `{ promise, cancel }`.
 * - `priority: 'high'` puts the request at the front of the queue (images the user opened).
 * - `cancel()` tells the queue this caller no longer needs the file; when nobody else is
 *   waiting for it, a queued request is dropped and a running one is aborted.
 */
export function requestAuthenticatedBlobUrl(value, { priority = 'normal' } = {}) {
  const noop = () => {};
  const trimmed = String(value || '').trim();
  if (!trimmed) return { promise: Promise.resolve(''), cancel: noop };
  if (isDirectMediaSrc(trimmed)) return { promise: Promise.resolve(trimmed), cancel: noop };

  const fetchUrl = resolveMediaFetchUrl(trimmed);
  if (!fetchUrl) return { promise: Promise.resolve(''), cancel: noop };

  const cached = blobCache.get(fetchUrl);
  if (cached) return { promise: Promise.resolve(cached), cancel: noop };

  let entry = inFlightFetches.get(fetchUrl);
  if (!entry) {
    entry = createEntry(fetchUrl);
    inFlightFetches.set(fetchUrl, entry);
    if (priority === 'high') pendingQueue.unshift(entry);
    else pendingQueue.push(entry);
  } else if (priority === 'high' && !entry.started) {
    const index = pendingQueue.indexOf(entry);
    if (index > 0) {
      pendingQueue.splice(index, 1);
      pendingQueue.unshift(entry);
    }
  }
  entry.waiters += 1;
  pumpQueue();

  let released = false;
  const cancel = () => {
    if (released) return;
    released = true;
    entry.waiters -= 1;
    if (entry.waiters > 0) return;
    if (inFlightFetches.get(fetchUrl) === entry) {
      inFlightFetches.delete(fetchUrl);
    }
    if (!entry.started) {
      const index = pendingQueue.indexOf(entry);
      if (index >= 0) pendingQueue.splice(index, 1);
      entry.reject(cancelledError());
    } else if (entry.controller) {
      entry.controller.abort();
    }
  };

  return { promise: entry.promise, cancel };
}

export async function fetchAuthenticatedBlobUrl(value) {
  // Direct callers (company logo, "open file" links) are user-facing: go first in the queue.
  return requestAuthenticatedBlobUrl(value, { priority: 'high' }).promise;
}

export async function openAuthenticatedFile(value) {
  const blobUrl = await fetchAuthenticatedBlobUrl(value);
  if (blobUrl) {
    window.open(blobUrl, '_blank', 'noopener,noreferrer');
  }
}

export function revokeMediaBlobCache() {
  blobCache.forEach((blobUrl) => {
    if (blobUrl.startsWith('blob:')) {
      URL.revokeObjectURL(blobUrl);
    }
  });
  blobCache.clear();
  pendingQueue.splice(0).forEach((entry) => entry.reject(cancelledError()));
  inFlightFetches.forEach((entry) => {
    if (entry.started && entry.controller) entry.controller.abort();
  });
  inFlightFetches.clear();
}

export function invalidateMediaBlobCacheFor(value) {
  const fetchUrl = resolveMediaFetchUrl(value);
  if (!fetchUrl) {
    return;
  }
  const cached = blobCache.get(fetchUrl);
  if (cached && cached.startsWith('blob:')) {
    URL.revokeObjectURL(cached);
  }
  blobCache.delete(fetchUrl);
  inFlightFetches.delete(fetchUrl);
}

export { API_BASE_URL };
